// PhoneService — client for the "Paiper Phone" continuation-link contract.
// A phone-call agent texts the customer a short link (paiperclip.com/c/TOKEN)
// so they can pick up on the web: either resuming the same chat/voice
// conversation the phone call is populating live (kind "session"), or just
// dropping in reference photos the agent asked for (kind "photos").

import AuthService from './AuthService';

export type PhoneContinuationReason = 'photo' | 'content' | 'payment' | 'review' | null;

interface ContinuationCommon {
  formId: string;
  channelId: string;
  businessName: string;
  callerUid: string;
  customToken: string;
  callActive: boolean;
  reason: PhoneContinuationReason;
  reportId?: string;
  expiresAt: number;
}

export interface ContinuationSession extends ContinuationCommon {
  kind: 'session';
}

export interface ContinuationPhotos extends ContinuationCommon {
  kind: 'photos';
  /** The photo question(s) the agent asked about (may be empty on older links). */
  questions?: string[];
}

/** Rebook / offer links (booking). No customToken: the browser stays an
    anonymous customer and calls redeemRebook to bind itself to the report. */
export interface OfferedTime {
  start: string;
  end: string;
}

export interface ContinuationRebook {
  kind: 'rebook' | 'offer';
  formId: string;
  channelId: string;
  businessName: string;
  reportId?: string;
  offered?: OfferedTime[] | null;
  customToken?: string | null;
  expiresAt: number;
}

export type ContinuationResponse = ContinuationSession | ContinuationPhotos | ContinuationRebook;

export interface RebookRedeemResponse {
  ok: true;
  formId: string;
  reportId?: string;
  offered?: OfferedTime[] | null;
}

export interface CallStatusResponse {
  callActive: boolean;
  status: string;
}

export interface AttachPhotosResponse {
  ok: true;
  imageUrls: string[];
}

/** Thrown when the continuation token is unknown or past its TTL (server
    404/410). `formId` is present when the server could still identify the
    form the link belonged to, so the caller can send the customer there. */
export class ContinuationExpiredError extends Error {
  formId?: string;

  constructor(formId?: string) {
    super('This continuation link has expired.');
    this.name = 'ContinuationExpiredError';
    this.formId = formId;
  }
}

/** Thrown when the attach endpoint rejects an image URL as outside the
    allowed Storage prefix (server 400). */
export class PhoneAttachRejectedError extends Error {
  constructor(message = 'One of those photos could not be attached.') {
    super(message);
    this.name = 'PhoneAttachRejectedError';
  }
}

export async function fetchContinuation(token: string): Promise<ContinuationResponse> {
  const response = await fetch(`/api/phone/continuation/${encodeURIComponent(token)}`);

  if (response.status === 404 || response.status === 410) {
    const body = await safeJson(response);
    throw new ContinuationExpiredError(body?.formId);
  }

  if (!response.ok) {
    throw new Error(`Failed to load continuation link (${response.status}).`);
  }

  return response.json();
}

/** Binds this (anonymous) customer to the link's report so the agent can find
    their earlier submission, then returns the form to open. */
export async function redeemRebook(token: string): Promise<RebookRedeemResponse> {
  const idToken = await AuthService.getInstance().getIdToken();
  if (!idToken) {
    throw new Error('Could not sign in.');
  }

  const response = await fetch('/api/rebook/redeem', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({ token }),
  });

  if (response.status === 404 || response.status === 410) {
    const body = await safeJson(response);
    throw new ContinuationExpiredError(body?.formId ?? body?.error?.formId);
  }

  if (!response.ok) {
    throw new Error(`Failed to open booking link (${response.status}).`);
  }

  return response.json();
}

export async function fetchCallStatus(token: string): Promise<CallStatusResponse> {
  const response = await fetch(`/api/phone/call-status/${encodeURIComponent(token)}`);
  if (!response.ok) {
    throw new Error(`Failed to check call status (${response.status}).`);
  }
  return response.json();
}

export async function attachPhonePhotos(
  token: string,
  imageUrls: string[]
): Promise<AttachPhotosResponse> {
  const response = await fetch('/api/phone/attach', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, imageUrls }),
  });

  if (response.status === 410) {
    const body = await safeJson(response);
    throw new ContinuationExpiredError(body?.formId);
  }

  if (response.status === 400) {
    throw new PhoneAttachRejectedError();
  }

  if (!response.ok) {
    throw new Error(`Failed to send photos (${response.status}).`);
  }

  return response.json();
}

async function safeJson(response: Response): Promise<any | null> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}
